import {
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  signIn,
  signInWithRedirect,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';
import { create } from 'zustand';

import { isOAuthConfigured } from '@/config/amplify';
import { queryClient } from '@/config/queryClient';
import { useReaderStore } from '@/features/reader/readerStore';
import api, { getErrorMessage, setUnauthorizedHandler } from '@/hooks/useApi';
import {
  identifyUser,
  resetUser,
  trackAuthError,
  trackSignInCompleted,
  trackSignInStarted,
} from '@/services/analytics';
import { logger } from '@/utils/logger';

export interface AuthUser {
  userId: string;
  email: string;
  name?: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  mfaRequired: boolean;
  verificationRequired: boolean;
  verificationEmail: string | null;

  // Actions
  loginWithEmail: (email: string, password: string) => Promise<void>;
  confirmMfa: (code: string) => Promise<void>;
  registerWithEmail: (email: string, password: string) => Promise<void>;
  confirmEmailVerification: (email: string, code: string) => Promise<void>;
  resendVerificationCode: (email: string) => Promise<void>;
  loginWithApple: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  getValidToken: () => Promise<string | null>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => {
  // Set up unauthorized handler to clear auth state on 401
  setUnauthorizedHandler(() => {
    get().logout();
  });

  return {
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    mfaRequired: false,
    verificationRequired: false,
    verificationEmail: null,

    loginWithEmail: async (email: string, password: string) => {
      set({ isLoading: true, error: null });
      trackSignInStarted('email');
      try {
        const { isSignedIn, nextStep } = await signIn({ username: email, password });

        if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          set({ mfaRequired: true, isLoading: false });
          return;
        }

        if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
          set({ verificationRequired: true, verificationEmail: email, isLoading: false });
          return;
        }

        if (!isSignedIn) {
          throw new Error('Sign in failed');
        }

        await get().checkAuthStatus();
        // Only track completion if auth check succeeded
        if (get().isAuthenticated) {
          trackSignInCompleted('email', false);
        } else {
          trackAuthError('email', 'session_check', 'Session check failed after sign in');
        }
      } catch (err) {
        const message = getErrorMessage(err, 'Login failed');
        const code = err instanceof Error ? err.name : undefined;
        trackAuthError('email', 'token_exchange', message, code);
        set({ error: message, isLoading: false });
        throw err;
      }
    },

    confirmMfa: async (code: string) => {
      set({ isLoading: true, error: null });
      try {
        const { isSignedIn } = await confirmSignIn({ challengeResponse: code });

        if (!isSignedIn) {
          throw new Error('MFA verification failed');
        }

        set({ mfaRequired: false });
        await get().checkAuthStatus();
        if (get().isAuthenticated) {
          trackSignInCompleted('email', false);
        }
      } catch (err) {
        const message = getErrorMessage(err, 'MFA verification failed');
        const code_ = err instanceof Error ? err.name : undefined;
        trackAuthError('email', 'token_exchange', message, code_);
        set({ error: message, isLoading: false });
        throw err;
      }
    },

    registerWithEmail: async (email: string, password: string) => {
      set({ isLoading: true, error: null });
      trackSignInStarted('email');
      try {
        // Call backend to register — user will need to verify email
        await api.post<{
          verificationRequired: boolean;
          deliveryDestination?: string;
        }>('/auth/register', {
          email,
          password,
        });

        // Registration successful — user must verify email before signing in
        set({ verificationRequired: true, verificationEmail: email, isLoading: false });
      } catch (err) {
        const message = getErrorMessage(err, 'Registration failed');
        const code = err instanceof Error ? err.name : undefined;
        trackAuthError('email', 'registration', message, code);
        set({ error: message, isLoading: false });
        throw err;
      }
    },

    confirmEmailVerification: async (email: string, code: string) => {
      set({ isLoading: true, error: null });
      try {
        await confirmSignUp({ username: email, confirmationCode: code });
        set({ verificationRequired: false, verificationEmail: null, isLoading: false });
      } catch (err) {
        const message = getErrorMessage(err, 'Verification failed');
        set({ error: message, isLoading: false });
        throw err;
      }
    },

    resendVerificationCode: async (email: string) => {
      set({ error: null });
      try {
        await resendSignUpCode({ username: email });
      } catch (err) {
        const message = getErrorMessage(err, 'Failed to resend code');
        set({ error: message });
        throw err;
      }
    },

    loginWithApple: async () => {
      if (!isOAuthConfigured()) {
        trackAuthError('apple', 'redirect', 'OAuth is not configured', 'OAUTH_NOT_CONFIGURED');
        set({ error: 'OAuth is not configured', isLoading: false });
        return;
      }
      set({ isLoading: true, error: null });
      trackSignInStarted('apple');
      try {
        // Redirects to Cognito hosted UI with Apple as identity provider
        // User will return to redirectSignIn URL after authentication
        // Amplify automatically handles the OAuth callback and token exchange
        await signInWithRedirect({ provider: 'Apple' });
      } catch (err) {
        const message = getErrorMessage(err, 'Sign in failed');
        const code = err instanceof Error ? err.name : undefined;
        trackAuthError('apple', 'redirect', message, code);
        set({ error: message, isLoading: false });
        throw err;
      }
    },

    loginWithGoogle: async () => {
      if (!isOAuthConfigured()) {
        trackAuthError('google', 'redirect', 'OAuth is not configured', 'OAUTH_NOT_CONFIGURED');
        set({ error: 'OAuth is not configured', isLoading: false });
        return;
      }
      set({ isLoading: true, error: null });
      trackSignInStarted('google');
      try {
        // Redirects to Cognito hosted UI with Google as identity provider
        // User will return to redirectSignIn URL after authentication
        // Amplify automatically handles the OAuth callback and token exchange
        await signInWithRedirect({ provider: 'Google' });
      } catch (err) {
        const message = getErrorMessage(err, 'Sign in failed');
        const code = err instanceof Error ? err.name : undefined;
        trackAuthError('google', 'redirect', message, code);
        set({ error: message, isLoading: false });
        throw err;
      }
    },

    logout: async () => {
      try {
        // Clear reader progress (localStorage) to prevent cross-user pollution
        useReaderStore.getState().clearAllProgress();
        // Clear TanStack Query cache to prevent data from previous user showing
        queryClient.clear();
        // Reset analytics user identity
        resetUser();
        // Sign out from Amplify (handles both email/password and OAuth users)
        await amplifySignOut();
      } finally {
        set({
          user: null,
          isAuthenticated: false,
          error: null,
        });
      }
    },

    checkAuthStatus: async () => {
      set({ isLoading: true });
      try {
        // Get current authenticated user from Amplify
        // This works for both email/password and OAuth sign-in
        const currentUser = await getCurrentUser();
        const session = await fetchAuthSession();

        const idToken = session.tokens?.idToken;
        const email =
          (idToken?.payload?.email as string) ||
          currentUser.signInDetails?.loginId ||
          currentUser.username;
        const name = (idToken?.payload?.name as string) || undefined;

        // Determine auth provider from identities claim (for federated users)
        let authProvider: 'apple' | 'google' | 'email' = 'email';
        const identities = idToken?.payload?.identities;
        if (typeof identities === 'string') {
          try {
            const parsed = JSON.parse(identities);
            if (Array.isArray(parsed) && parsed.length > 0) {
              const provider = parsed[0].providerName?.toLowerCase();
              if (provider === 'signinwithapple') {
                authProvider = 'apple';
              } else if (provider === 'google') {
                authProvider = 'google';
              }
            }
          } catch (_error) {
            // Ignore parse errors, default to email
          }
        }

        const user: AuthUser = {
          userId: currentUser.userId,
          email,
          name,
        };

        // Identify user in analytics (no PII - just user ID)
        identifyUser(currentUser.userId, authProvider);

        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } catch (err) {
        logger.error('Failed to check auth status:', err);
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    },

    getValidToken: async () => {
      try {
        // Amplify handles token refresh automatically
        const session = await fetchAuthSession({ forceRefresh: false });
        return session.tokens?.idToken?.toString() ?? null;
      } catch (err) {
        logger.error('Failed to get valid token:', err);
        return null;
      }
    },

    clearError: () => {
      if (get().error !== null) {
        set({ error: null });
      }
    },
  };
});
